import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import { RS_BREADCRUMBS_LOCAL_STORAGE_KEY } from '@Pages/RetroSynthesis/RsBreadcrumbs/constants';

import RsBreadcrumbItem from '@Pages/RetroSynthesis/RsBreadcrumbs/RsBreadcrumbItem';
import isEmpty from 'lodash/isEmpty';
import forEach from 'lodash/forEach';
import { getUrlParamsArrayByName, getValueFromStorage, putValueToStorage, removeValueFromStorage } from '@Pages/RetroSynthesis/utils';
import { PageName, RsRoute } from '@Pages/RetroSynthesis/routes';
import difference from 'lodash/difference';
import isEqual from 'lodash/isEqual';
import sortBy from 'lodash/sortBy';
import filter from 'lodash/filter';
import { UrlParams, RouteType } from '@Pages/RetroSynthesis/constants';
import {
  DrawTooltipContent,
  ProjectListTooltipContent,
  ProjectTooltipContent,
  ProjectRouteTooltipContent,
} from '@Pages/RetroSynthesis/RsBreadcrumbs/RsBreadcrumbsTooltipContent';
import { splitLabelIntoLines } from '@Pages/RetroSynthesis/RsBreadcrumbs/utils';
import { ProjectRoutesCountMapState } from '@Pages/RetroSynthesis/RsBreadcrumbs/reducer';

import './RsBreadcrumbs.theme.scss';

/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-use-before-define */
const getData = () => getValueFromStorage(RS_BREADCRUMBS_LOCAL_STORAGE_KEY, true) || [];

const putData = (value) => putValueToStorage(RS_BREADCRUMBS_LOCAL_STORAGE_KEY, value, true);

export const clearRsBreadcrumbs = () => removeValueFromStorage(RS_BREADCRUMBS_LOCAL_STORAGE_KEY, true);

const getPartAt = (index, parts = []) => (parts.length > index ? parts[index] : undefined);

const getRouteBreadcrumbClassName = (routeType) => {
  switch (routeType) {
    case RouteType.PUBLISHED:
      return 'breadcrumb-published-route';
    case RouteType.PREDICTED:
      return 'breadcrumb-predicted-route';
    case RouteType.CUSTOMIZED:
      return 'breadcrumb-customized-route';
    default:
      return undefined;
  }
};

const getRouteBreadcrumbLabel = (routeType, routeId) => {
  switch (routeType) {
    case RouteType.PUBLISHED:
      return `Published Route #${routeId}`;
    case RouteType.PREDICTED:
      return `Predicted Route #${routeId}`;
    case RouteType.CUSTOMIZED:
      return `Custom Route #${routeId}`;
    default:
      return `Route #${routeId}`;
  }
};

export const breadcrumbDraw = (active) => ({
  link: RsRoute.DRAW_STRUCTURE,
  label: 'Draw',
  iconName: 'draw-structure',
  className: 'breadcrumb-draw-structure',
  children: <DrawTooltipContent />,
  ariaLabel: 'Breadcrumb link to Draw structure',
  active,
});

// eslint-disable-next-line default-param-last
export const breadcrumbProjects = (projectsCount = 0, active) => ({
  link: RsRoute.PROJECTS_LIST,
  label: 'My Synthesis Projects',
  iconName: 'projects-list',
  children: <ProjectListTooltipContent projectsCount={projectsCount} />,
  ariaLabel: 'Breadcrumb link to my synthesis projects',
  active,
});

const formatProjectBreadcrumbLabel = (label) => {
  const lines = splitLabelIntoLines(label);
  return lines.map((line, idx) => (
    // eslint-disable-next-line react/no-array-index-key
    <React.Fragment key={idx}>
      {line}
      {idx < lines.length - 1 && <br />}
    </React.Fragment>
  ));
};

// `Project No #${projectId} ${breadcrumbProjectTooltipText(routesCount)}`
export const breadcrumbProject = (projectId, routesInfo, active) => ({
  link: RsRoute.getProjectLink(projectId),
  label: routesInfo && routesInfo.projectName ? formatProjectBreadcrumbLabel(routesInfo.projectName) : '',
  children: <ProjectTooltipContent routesInfo={routesInfo} />,
  ariaLabel: `Breadcrumb link to ${routesInfo && routesInfo.projectName}`,
  active,
});

export const breadcrumbProjectLimited = (projectId, limitTo, active) => ({
  link: RsRoute.getProjectLinkLimitTo(projectId, limitTo),
  label: `${limitTo.length}`,
  children: `Project No #${projectId} limited by routes: ${limitTo}`,
  ariaLabel: `Breadcrumb link to project No #${projectId} limited by routes: ${limitTo}`,
  active,
});

export const breadcrumbProjectRoute = (projectId, routeId, projectInfo, active, routeType, routeView) => ({
  className: getRouteBreadcrumbClassName(routeType),
  link: RsRoute.getProjectRouteLink(projectId, routeId, routeView),
  label: getRouteBreadcrumbLabel(routeType, routeId),
  children: <ProjectRouteTooltipContent routeLabel={getRouteBreadcrumbLabel(routeType, routeId)} projectInfo={projectInfo} />,
  ariaLabel: `Breadcrumb link to ${getRouteBreadcrumbLabel(routeType, routeId)}`,
  active,
});

const isSubsetArray = (subset, superset) => difference(subset, superset).length === 0;

const isEqualArray = (a, b) => isEqual(sortBy(a), sortBy(b));

const defineNewLimits = (newLimit, limits) => {
  if (isEmpty(newLimit)) {
    return;
  }
  const foundInArray = !isEmpty(filter(limits, (a) => isEqualArray(newLimit, a)));
  if (foundInArray) {
    // eslint-disable-next-line consistent-return
    return limits;
  }
  const result = [];
  forEach(limits, (lim) => {
    if (!isEqualArray(newLimit, lim) && isSubsetArray(newLimit, lim)) {
      result.push(lim);
    }
  });
  result.push(newLimit);
  // eslint-disable-next-line consistent-return
  return result;
};

const handleUrlChanges = (location, { removedProjectIds, removedRouteIds }) => {
  const oldData = getData();
  if (!location) {
    return oldData;
  }
  // eslint-disable-next-line no-use-before-define
  const newData = parseLocation(location);

  if (newData && !newData.isRS) {
    return {};
  }

  if (oldData.isProjects && newData.isProjects && !newData.routeId) {
    if (removedRouteIds && removedRouteIds.length && newData.limit && newData.limit.length) {
      // if delete routes happened after limit to
      if (removedRouteIds.length === newData.limit.length) {
        // remove all limited routes
        newData.limit = [];
        newData.limits = [];
      } else {
        // partial removal of limited routes
        newData.limits = [newData.limit];
      }
    } else {
      newData.limits = defineNewLimits(newData.limit, oldData.limits);
    }
  } else if (newData.limit) {
    newData.limits = [newData.limit];
  }
  Object.keys(newData).forEach((key) => newData[key] === undefined && delete newData[key]);
  const result = { ...oldData, ...newData, limit: newData.limit };
  if (oldData.projectId && newData.projectId && oldData.projectId !== newData.projectId && !newData.routeId) {
    result.limit = undefined;
    result.limits = undefined;
    result.routeId = undefined;
  } else if (oldData.routeId !== result.routeId && result.limits) {
    result.limits = result.limits.filter((limit) => isSubsetArray([newData.routeId], limit));
  }
  if (!newData.routeId && newData.limits && !isSubsetArray([oldData.routeId], newData.limit)) {
    result.routeId = undefined;
  }

  // when routes or projects are removed, check if any of them are present in breadcrumbs, and remove them
  if (removedProjectIds && !isEmpty(removedProjectIds) && removedProjectIds.some((id) => String(id) === result.projectId)) {
    if (removedRouteIds && !isEmpty(removedRouteIds) && removedRouteIds.some((id) => String(id) === result.routeId)) {
      // remove route from breadcrumbs
      result.limit = undefined;
      result.limits = undefined;
      result.routeId = undefined;
      result.routeView = undefined;
    } else if (isEmpty(removedRouteIds)) {
      // remove project from breadcrumbs
      result.limit = undefined;
      result.limits = undefined;
      result.routeId = undefined;
      result.routeView = undefined;
      result.projectId = undefined;
    }
  }

  putData(result);
  return result;
};

const parseLocation = (location) => {
  const result = {};
  const parts = (location && location.pathname && location.pathname.split('/').filter((p) => !isEmpty(p))) || [];

  result.isRS = getPartAt(0, parts) === PageName.RETRO_SYNTHESIS;
  result.isProjects = getPartAt(1, parts) === PageName.MY_PROJECTS;
  result.projectId = getPartAt(2, parts);
  result.limit = getUrlParamsArrayByName(location, UrlParams.LIMIT_TO);
  result.routeId = getPartAt(3, parts);
  result.routeView = getPartAt(4, parts);
  return result;
};

function RsBreadcrumbs({
  projectsCount,
  projectsInfoMap,
  location,
  history,
  fetchProjectInfo,
  routeType,
  removedProjectIds,
  removedRouteIds,
}) {
  const [data, setData] = useState(handleUrlChanges(location, { routeType, removedProjectIds, removedRouteIds }));

  useEffect(() => {
    // eslint-disable-next-line no-shadow
    return history.listen((location) => {
      setData(handleUrlChanges(location, { routeType, removedProjectIds, removedRouteIds }));
    });
  }, [history]);

  useEffect(() => {
    setData(handleUrlChanges(location, { routeType, removedProjectIds, removedRouteIds }));
  }, [removedProjectIds, removedRouteIds]);

  const generateItems = () => {
    const { isRS, projectId, limit, limits, routeId, routeView } = data;
    const loc = parseLocation(location);
    const result = [];
    const hasProjects = projectsCount && projectsCount > 0;
    if (!isRS) {
      return result;
    }
    result.unshift(breadcrumbDraw(!loc.isProjects));
    result.unshift({
      ...breadcrumbProjects(projectsCount, loc.isProjects && isEmpty(loc.projectId)),
      disabled: !hasProjects,
    });

    if (!isEmpty(projectId)) {
      const projectInfo = projectsInfoMap[projectId];
      if (!projectInfo) {
        fetchProjectInfo(projectId);
      }
      if (projectInfo !== ProjectRoutesCountMapState.ERROR) {
        result.unshift(breadcrumbProject(projectId, projectInfo, !isEmpty(loc.projectId) && isEmpty(loc.limit) && isEmpty(loc.routeId)));
      }
      forEach(limits, (lm) => {
        result.unshift(breadcrumbProjectLimited(projectId, lm, isEqualArray(lm, limit)));
      });
      if (!isEmpty(routeId) && projectInfo !== ProjectRoutesCountMapState.ERROR) {
        result.unshift(breadcrumbProjectRoute(projectId, routeId, projectInfo, !isEmpty(loc.routeId), routeType, routeView));
      }
    }
    return result;
  };

  const items = generateItems();

  return (
    <div className="new-breadcrumbs rs-breadcrumbs">
      <ul className="new-breadcrumbs__list">
        {items.map((item, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <RsBreadcrumbItem {...item} key={`breadcrumb-item-${i}`} />
        ))}
      </ul>
    </div>
  );
}

RsBreadcrumbs.defaultProps = {
  projectsInfoMap: {},
  fetchProjectInfo: () => null,
};

RsBreadcrumbs.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      link: PropTypes.string.isRequired,
      iconName: PropTypes.string,
      label: PropTypes.string,
      className: PropTypes.string,
    })
  ),
  projectsInfoMap: PropTypes.object,
  history: PropTypes.object,
  location: PropTypes.object,
  projectsCount: PropTypes.number,
  fetchProjectInfo: PropTypes.func,
  routeType: PropTypes.string,
  removedProjectIds: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  removedRouteIds: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
};

export default RsBreadcrumbs;
